import { useState, useEffect, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';
import { toast } from 'react-hot-toast';
import { notificationService } from '@/services/notifications';
import { Notification, NotificationType } from '@/types/notification';
import { useAuth } from '@/contexts/AuthContext';
import { logger } from '@/utils/logger';

export function useNotifications() {
  const { user, token } = useAuth();
  const [socket, setSocket] = useState<Socket | null>(null);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  // Initialize WebSocket connection
  useEffect(() => {
    if (!user || !token) return;

    const wsUrl = import.meta.env.VITE_WS_URL || 'https://prompthub-api.surfinn.kr';
    const newSocket = io(wsUrl, {
      transports: ['websocket', 'polling'],
      auth: {
        token,
      },
    });

    // Connection events
    newSocket.on('connect', () => {
      console.log('✅ WebSocket connected', {
        id: newSocket.id,
        userId: user.id,
        transport: newSocket.io.engine.transport.name
      });
      logger.info('WebSocket connected', { socketId: newSocket.id });
    });
    
    newSocket.on('connected', (data) => {
      console.log('🚀 WebSocket welcome message:', data);
      logger.info('WebSocket welcome message', data);
    });

    // Handle new notifications
    newSocket.on('notification', (data) => {
      console.log('📨 WebSocket notification received:', data);
      logger.info('WebSocket notification received', data);
      
      if (data.type === 'NEW_NOTIFICATION') {
        const notification = data.notification;
        console.log('🔔 New notification:', notification);
        
        // Add to notifications list
        setNotifications(prev => [notification, ...prev]);
        
        // Increment unread count
        setUnreadCount(prev => prev + 1);
        
        // Show toast notification
        showNotificationToast(notification);
        
        // Play notification sound
        playNotificationSound();
      } else if (data.type === 'NOTIFICATION_READ') {
        // Mark notification as read
        setNotifications(prev =>
          prev.map(n =>
            n.id === data.notificationId ? { ...n, isRead: true } : n
          )
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
      } else if (data.type === 'ALL_NOTIFICATIONS_READ') {
        // Mark all as read
        setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
        setUnreadCount(0);
      } else if (data.type === 'NOTIFICATION_DELETED') {
        // Remove notification
        setNotifications(prev => prev.filter(n => n.id !== data.notificationId));
      }
    });

    newSocket.on('disconnect', () => {
      logger.info('WebSocket disconnected');
    });

    newSocket.on('error', (error) => {
      logger.error('WebSocket error', error);
    });

    setSocket(newSocket);

    return () => {
      newSocket.disconnect();
    };
  }, [user, token]);

  // Load initial notifications
  useEffect(() => {
    if (user) {
      loadNotifications();
    }
  }, [user]);

  const loadNotifications = useCallback(async () => {
    try {
      setIsLoading(true);
      const result = await notificationService.getNotifications({
        page: 1,
        limit: 20,
      });
      
      setNotifications(result.notifications);
      setUnreadCount(result.unreadCount);
      setHasMore(result.pagination.page < result.pagination.totalPages);
    } catch (error) {
      logger.error('Failed to load notifications', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loadMore = useCallback(async () => {
    if (!hasMore || isLoading) return;

    try {
      const nextPage = page + 1;
      const result = await notificationService.getNotifications({
        page: nextPage,
        limit: 20,
      });
      
      setNotifications(prev => [...prev, ...result.notifications]);
      setPage(nextPage);
      setHasMore(result.pagination.page < result.pagination.totalPages);
    } catch (error) {
      logger.error('Failed to load more notifications', error);
    }
  }, [page, hasMore, isLoading]);

  const markAsRead = useCallback(async (notificationId: string) => {
    try {
      await notificationService.markAsRead(notificationId);
      
      setNotifications(prev =>
        prev.map(n =>
          n.id === notificationId ? { ...n, isRead: true } : n
        )
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      logger.error('Failed to mark notification as read', error);
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    try {
      await notificationService.markAllAsRead();
      
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch (error) {
      logger.error('Failed to mark all notifications as read', error);
      throw error;
    }
  }, []);

  const deleteNotification = useCallback(async (notificationId: string) => {
    try {
      await notificationService.deleteNotification(notificationId);
      
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      
      const notification = notifications.find(n => n.id === notificationId);
      if (notification && !notification.isRead) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      logger.error('Failed to delete notification', error);
      throw error;
    }
  }, [notifications]);

  const deleteAllNotifications = useCallback(async () => {
    try {
      await notificationService.deleteAllNotifications();
      
      setNotifications([]);
      setUnreadCount(0);
    } catch (error) {
      logger.error('Failed to delete all notifications', error);
      throw error;
    }
  }, []);

  return {
    notifications,
    unreadCount,
    isLoading,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    deleteAllNotifications,
    loadMore,
    hasMore,
    refresh: loadNotifications,
  };
}

// Helper function to show notification toast
function showNotificationToast(notification: Notification) {
  const icon = getNotificationIcon(notification.type);
  
  // Use simple toast instead of custom JSX
  toast(`${icon} ${notification.title}\n${notification.message}`, {
    duration: 5000,
    position: 'top-right',
    style: {
      background: '#fff',
      color: '#333',
      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
      padding: '16px',
      borderRadius: '8px',
    },
  });
}

// Helper function to get notification icon
function getNotificationIcon(type: NotificationType) {
  switch (type) {
    case 'COMMENT':
      return '💬';
    case 'LIKE':
      return '❤️';
    case 'FOLLOW':
      return '👥';
    case 'WORKFLOW_FORKED':
      return '🔀';
    case 'SYSTEM':
      return 'ℹ️';
    default:
      return '🔔';
  }
}

// Helper function to play notification sound
function playNotificationSound() {
  try {
    const audio = new Audio('/sounds/notification.mp3');
    audio.volume = 0.5;
    audio.play().catch(error => {
      // Ignore errors if autoplay is blocked
      logger.debug('Could not play notification sound', error);
    });
  } catch (error) {
    logger.debug('Notification sound not available', error);
  }
}